/**
 * Service Worker Registration for One Voice PWA
 */

import { logger } from '@/lib/logger';

export interface ServiceWorkerRegistrationResult {
  registration: ServiceWorkerRegistration | null;
  error: Error | null;
}

/**
 * Register the service worker
 */
export async function registerServiceWorker(): Promise<ServiceWorkerRegistrationResult> {
  // Check if service workers are supported
  if (typeof window === 'undefined' || !('serviceWorker' in navigator)) {
    logger.warn('[SW] Service workers are not supported');
    return { registration: null, error: null };
  }

  const isDev = import.meta.env.DEV;
  if (isDev) {
    logger.debug('[SW] Skipping service worker registration in development');
    return { registration: null, error: null };
  }

  try {
    // Wait for the page to load
    if (document.readyState !== 'complete') {
      await new Promise<void>((resolve) => {
        window.addEventListener('load', () => resolve());
      });
    }

    const registration = await navigator.serviceWorker.register('/sw.js', {
      scope: '/',
      updateViaCache: 'none',
    });

    logger.info('[SW] Service worker registered:', registration.scope);

    setupUpdateHandling(registration);

    return { registration, error: null };
  } catch (error) {
    logger.error('[SW] Service worker registration failed:', error);
    return {
      registration: null,
      error: error instanceof Error ? error : new Error('Unknown error'),
    };
  }
}

/**
 * Unregister all service workers
 */
export async function unregisterServiceWorker(): Promise<boolean> {
  if (typeof window === 'undefined' || !('serviceWorker' in navigator)) {
    return false;
  }

  try {
    const registration = await navigator.serviceWorker.ready;
    const success = await registration.unregister();
    logger.info('[SW] Service worker unregistered:', success);
    return success;
  } catch (error) {
    logger.error('[SW] Failed to unregister service worker:', error);
    return false;
  }
}

/**
 * Set up handling for service worker updates
 */
function setupUpdateHandling(registration: ServiceWorkerRegistration): void {
  // Check for updates periodically (every hour)
  const UPDATE_INTERVAL = 60 * 60 * 1000; // 1 hour

  setInterval(() => {
    registration.update().catch((err) => logger.error('[SW] Update check failed:', err));
  }, UPDATE_INTERVAL);

  registration.addEventListener('updatefound', () => {
    const newWorker = registration.installing;

    if (!newWorker) return;

    logger.debug('[SW] New service worker found');

    newWorker.addEventListener('statechange', () => {
      if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
        logger.info('[SW] New service worker available');

        // Dispatch custom event for the app to handle
        window.dispatchEvent(
          new CustomEvent('sw-update-available', {
            detail: { registration },
          })
        );
      }
    });
  });

  navigator.serviceWorker.addEventListener('controllerchange', () => {
    logger.info('[SW] Controller changed');
    // Optionally reload the page when a new service worker takes control
    // window.location.reload();
  });
}

/**
 * Trigger the waiting service worker to activate
 */
export function skipWaiting(registration: ServiceWorkerRegistration): void {
  const waitingWorker = registration.waiting;

  if (waitingWorker) {
    waitingWorker.postMessage({ type: 'SKIP_WAITING' });
  }
}

/**
 * Get the current service worker registration
 */
export async function getRegistration(): Promise<ServiceWorkerRegistration | undefined> {
  if (typeof window === 'undefined' || !('serviceWorker' in navigator)) {
    return undefined;
  }

  return navigator.serviceWorker.ready;
}

/**
 * Check if a service worker is active
 */
export function isServiceWorkerActive(): boolean {
  if (typeof window === 'undefined' || !('serviceWorker' in navigator)) {
    return false;
  }

  return !!navigator.serviceWorker.controller;
}

/**
 * Send a message to the service worker (e.g. for SKIP_WAITING)
 */
export async function sendMessageToSW<T = unknown>(
  message: { type: string; data?: unknown }
): Promise<T | undefined> {
  if (typeof window === 'undefined' || !('serviceWorker' in navigator)) {
    return undefined;
  }

  const registration = await navigator.serviceWorker.ready;
  const controller = registration.active;

  if (!controller) {
    logger.warn('[SW] No active service worker');
    return undefined;
  }

  return new Promise((resolve) => {
    const messageChannel = new MessageChannel();
    messageChannel.port1.onmessage = (event) => resolve(event.data);
    controller.postMessage(message, [messageChannel.port2]);
    setTimeout(() => resolve(undefined), 5000);
  });
}
